// The review session: one object that holds the game open on the review
// screen and everything the panels read from it. It keeps the state (the
// game, the ply, the reader's own `Line`, a look back), loads the game's
// stored scan and opening book once, follows the scan as it grows, and
// derives what every panel would otherwise work out for itself: the stages,
// the reader's side, the theory moves from the position. Panels read it
// through `state()`, `subscribe` or `select`, and change it only through its
// methods.
import type { MoveInput } from '@chess/api-chess';
import type { Analysis, AnalysisEvent } from '@chess/api-analysis';
import type { OpeningContinuation, Openings } from '@chess/api-openings';
import { constraintScopeId, type ConstraintRegistry, type ConstraintScope } from '@chess/api-constraints';
import { Observable } from './observable.ts';
import { Line } from './line.ts';
import { Stages } from './stages.ts';
import { GameScan } from './game-scan.ts';
import { GameBook, movePrefixOf } from './game-book.ts';
import type { GameLoader, Review, ReviewGame, ReviewRules, ReviewState } from './index.ts';
import { type Color, type Fen, type MistakeRulesSource, type San, type Scan } from '@chess/api-model';

/** What the session may use beyond the game loader; each is optional. */
export interface ReviewServices {
  analysis?: Analysis;
  openings?: Openings;
  /** The constraint registry: the session enforces its own scope's Controls and Next move. */
  constraints?: ConstraintRegistry;
  /** The user's blunder and miss rules; the scan's verdicts follow them, and are re-read when they change. Without it, the defaults. */
  mistakeRules?: MistakeRulesSource;
}

/** The book's moves from the board's position, and whether they take the best-moves place. */
export interface ReviewTheory {
  /** The theory list shows, rather than the engine's lines. */
  active: boolean;
  moves: OpeningContinuation[];
  /** "5." or "5…" for the side to move. */
  movePrefix: string;
}

function clamp(ply: number, moveCount: number): number {
  if (!Number.isFinite(ply)) return 0;
  return Math.min(Math.max(Math.trunc(ply), 0), moveCount);
}

export class ReviewSession extends Observable<ReviewState> implements Review {
  private readonly load: GameLoader;
  private readonly rules?: ReviewRules;
  private services: ReviewServices;
  /** The reader's line, kept while its cursor is at 0 so the same move re-enters it. */
  private line?: Line;
  private token = 0;
  private stopScan?: () => void;
  private stopRules?: () => void;
  /** This board's constraint scope: a review tab. */
  readonly scope: ConstraintScope;

  constructor(load: GameLoader, rules?: ReviewRules, services: ReviewServices = {}, tabId = 'pinned') {
    const scope: ConstraintScope = { kind: 'reviewTab', id: constraintScopeId(tabId) };
    super({ status: 'idle', ply: 0, theory: { active: true, moves: [], movePrefix: '' }, bookStatus: 'idle', scope });
    this.scope = scope;
    this.load = load;
    this.rules = rules;
    this.services = services;
    this.followRules();
  }

  /** Release every constraint on this board, e.g. when its tab closes. */
  releaseConstraints(): void {
    this.services.constraints?.releaseScope(this.scope);
  }

  /**
   * Give the session the analysis or openings API once it is there. A game
   * already open gets its scan or book loaded at once; later games load both
   * when they open.
   */
  connect(services: ReviewServices): void {
    const before = this.services;
    this.services = { ...this.services, ...services };
    if (services.mistakeRules && services.mistakeRules !== before.mistakeRules) {
      this.followRules();
      const open = this.state().scan;
      if (open) this.commit({ scan: this.scanOf(open.scan) });
    }
    const game = this.state().game;
    if (!game) return;
    if (services.openings && !before.openings) {
      this.commit({ bookStatus: 'loading' });
      this.loadBook(game, this.token);
    }
    if (services.analysis && !before.analysis) this.followScan(game.id, this.token);
  }

  // ---- reading -------------------------------------------------------------

  /** The ply along the path the board shows. */
  viewPly(): number {
    const s = this.state();
    return s.browse ?? this.headPly();
  }

  /** The far end of the path: the line's cursor, or the game's ply. */
  headPly(): number {
    const s = this.state();
    return this.line && !this.line.empty ? this.line.headPly : s.ply;
  }

  /** The position on the board. */
  position(): Fen | undefined {
    const s = this.state();
    const game = s.game;
    if (!game) return undefined;
    const ply = s.browse ?? this.headPly();
    return this.pathPosition(ply);
  }

  /** The stage of the position on the board, for a game position; undefined while on the reader's line. */
  stage() {
    const s = this.state();
    if (!s.stages || s.exploring) return undefined;
    return s.stages.ofPosition(s.ply);
  }

  // ---- the Review interface ------------------------------------------------

  async open(gameId: string | undefined): Promise<void> {
    // Another game, or none: whatever constrained this board no longer applies.
    if (gameId !== this.state().gameId) this.releaseConstraints();
    // The game already open: nothing changes. The reader's line, the look
    // back, the scan being followed and the book stay as they are; a caller
    // that wants a ply (the address's `#ply=N`) calls `go` next, which
    // returns to the game there. Checked before anything is reset, so the
    // session never drops what its state still shows.
    if (gameId && this.state().gameId === gameId && this.state().status === 'ready') return;
    const mine = ++this.token;
    this.line = undefined;
    this.stopScan?.();
    this.stopScan = undefined;
    const cleared = { game: undefined, ply: 0, exploring: undefined, browse: undefined, error: undefined, stages: undefined, reader: undefined, scan: undefined, scanning: undefined, book: undefined };
    if (!gameId) {
      this.commit({ status: 'idle', gameId: undefined, ...cleared, bookStatus: 'idle' });
      return;
    }
    this.commit({ status: 'loading', gameId, ...cleared, bookStatus: 'loading' });
    try {
      const game = await this.load(gameId);
      if (mine !== this.token) return; // another open() won
      if (!game) {
        this.commit({ status: 'missing', bookStatus: 'idle' });
        return;
      }
      this.commit({
        status: 'ready',
        game,
        ply: 0,
        exploring: undefined,
        browse: undefined,
        stages: game.phases ? new Stages(game.phases, game.moves.length, game.sides?.reader) : undefined,
        reader: game.sides?.reader,
        bookStatus: this.services.openings ? 'loading' : 'unavailable',
      });
      this.loadBook(game, mine);
      this.followScan(game.id, mine);
    } catch (error) {
      if (mine !== this.token) return;
      this.commit({ status: 'failed', error: error instanceof Error ? error.message : String(error), bookStatus: 'idle' });
    }
  }

  go(ply: number): void {
    const s = this.state();
    if (!s.game) return;
    const clamped = clamp(ply, s.game.moves.length);
    if (clamped !== s.ply || this.line || s.browse !== undefined) {
      this.line = undefined;
      this.commit({ ply: clamped, browse: undefined });
    }
  }

  stepBackward(): void {
    if (this.guarded('back', () => this.browse(this.viewPly() - 1))) return;
    const s = this.state();
    if (s.browse !== undefined) return;
    if (this.line && !this.line.empty) {
      // Take back the line's last move; at its first move the board returns to the game.
      this.line = this.line.back();
      this.commit({});
      return;
    }
    this.step(s.ply - 1);
  }

  stepForward(): void {
    if (this.guarded('forward', () => this.browse(this.viewPly() + 1))) return;
    this.step(this.state().ply + 1);
  }

  toStart(): void {
    if (this.guarded('back', (range) => this.browse(range.from))) return;
    const s = this.state();
    if (this.line && !this.line.empty && s.browse === undefined) this.go(0);
    else this.step(0);
  }

  toEnd(): void {
    if (this.guarded('forward', (range) => this.browse(range.to))) return;
    this.step(this.state().game?.moves.length ?? 0);
  }

  play(input: MoveInput | San): boolean {
    const s = this.state();
    const game = s.game;
    if (s.browse !== undefined) return false; // looking back: nothing can be played
    const registry = this.services.constraints;
    if (registry && !registry.bypassed(this.scope) && registry.current(this.scope, 'nextMove').allow === 'none') return false;
    const fen = this.position();
    if (!game || !fen || !this.rules) return false;
    const played = this.rules.play(fen, input);
    if (!played) return false;
    const onLine = this.line && !this.line.empty;
    // The game's own next move is a step, not a deviation.
    if (!onLine && game.moves[s.ply]?.uci === played.move.uci) {
      this.go(s.ply + 1);
      return true;
    }
    const base = this.line && this.line.fromPly === s.ply ? this.line : new Line(s.ply, game.positions[s.ply]);
    this.line = base.play(played.move, played.fen);
    this.commit({});
    return true;
  }

  returnToGame(): void {
    if (!this.line || this.line.empty) return;
    const ply = this.line.fromPly;
    this.line = undefined;
    this.commit({ ply, browse: undefined });
  }

  browse(ply: number | undefined): void {
    if (!this.state().game) return;
    const head = this.headPly();
    let next = ply === undefined || !Number.isFinite(ply) ? undefined : Math.trunc(ply);
    // A Controls range holds the look within it, unless its owner is acting.
    const registry = this.services.constraints;
    const range = registry && !registry.bypassed(this.scope) ? registry.current(this.scope, 'controls').range : undefined;
    if (range && next !== undefined) next = Math.min(Math.max(next, range.from), range.to);
    const view = next === undefined || next >= head ? undefined : Math.max(next, 0);
    if (view !== this.state().browse) this.commit({ browse: view });
  }

  // ---- internals -----------------------------------------------------------

  /**
   * The Controls constraint on a step, unless an owner is acting: `true` when
   * it handled the step (refused it, or looked along the path within its
   * range), `false` when the step goes ahead as usual.
   */
  private guarded(direction: 'back' | 'forward', withinRange: (range: { from: number; to: number }) => void): boolean {
    const registry = this.services.constraints;
    if (!registry || registry.bypassed(this.scope)) return false;
    const controls = registry.current(this.scope, 'controls');
    if (!controls[direction]) return true;
    if (controls.range) {
      withinRange(controls.range);
      return true;
    }
    return false;
  }

  private step(ply: number): void {
    const s = this.state();
    if ((this.line && !this.line.empty) || s.browse !== undefined) return;
    this.go(ply);
  }

  private pathPosition(ply: number): Fen | undefined {
    const game = this.state().game;
    if (this.line && !this.line.empty && ply > this.line.fromPly) return this.line.positionAt(ply);
    return game?.positions[ply];
  }

  /** Apply a change, then re-derive the exploration and the theory from the whole state. */
  private commit(patch: Partial<ReviewState>): void {
    const s = { ...this.state(), ...patch };
    const exploring = this.line && !this.line.empty ? this.line.toExploration() : undefined;
    const next = { ...patch, exploring };
    this.update({ ...next, theory: this.theoryFor({ ...s, ...next }) });
  }

  private theoryFor(s: ReviewState): ReviewTheory {
    const empty = (active: boolean): ReviewTheory => ({ active, moves: [], movePrefix: '' });
    const game = s.game;
    if (!game) return empty(true);
    const atStart = s.ply === 0 && !s.exploring && s.browse === undefined;
    const fen = s.browse !== undefined ? this.pathPositionOf(s, s.browse) : s.exploring ? s.exploring.fen : game.positions[s.ply];
    if (!fen) return empty(false);
    if (s.bookStatus === 'loading') return empty(true);
    const book = s.book;
    if (!book) return empty(atStart);
    if (atStart) return { active: true, moves: book.movesFrom(fen, true), movePrefix: movePrefixOf(fen) };
    if (!book.knows(fen)) return empty(false);
    const moves = book.movesFrom(fen);
    // A position the book knows but has no move from is not theory: the engine's lines show.
    return moves.length ? { active: true, moves, movePrefix: movePrefixOf(fen) } : empty(false);
  }

  private pathPositionOf(s: ReviewState, ply: number): Fen | undefined {
    if (s.exploring && ply > s.exploring.fromPly) return s.exploring.positions[ply - s.exploring.fromPly];
    return s.game?.positions[ply];
  }

  private loadBook(game: ReviewGame, mine: number): void {
    const openings = this.services.openings;
    if (!openings) return;
    GameBook.load(openings, game)
      .then((book) => {
        if (mine === this.token) this.commit({ book, bookStatus: 'ready' });
      })
      .catch(() => {
        if (mine === this.token) this.commit({ book: undefined, bookStatus: 'unavailable' });
      });
  }

  /** The scan as the review reads it, under the user's rules. */
  private scanOf(scan: Scan): GameScan {
    return new GameScan(scan, this.services.mistakeRules?.get());
  }

  /** Re-reads the open scan's verdicts whenever the user changes the rules. */
  private followRules(): void {
    this.stopRules?.();
    const source = this.services.mistakeRules;
    if (!source) return;
    this.stopRules = source.subscribe(() => {
      const scan = this.state().scan;
      if (scan) this.commit({ scan: this.scanOf(scan.scan) });
    });
  }

  private followScan(gameId: string, mine: number): void {
    const analysis = this.services.analysis;
    if (!analysis) return;
    const refresh = (): void => {
      void analysis.get(gameId).then((scan) => {
        if (mine !== this.token || !scan) return;
        const status = analysis.status(gameId);
        this.commit({ scan: this.scanOf(scan), scanning: scan.complete ? undefined : { done: status?.done ?? scan.positions.length, total: status?.total ?? scan.total } });
      });
    };
    this.stopScan = analysis.subscribe((event: AnalysisEvent) => {
      if (mine !== this.token || event.gameId !== gameId) return;
      if (event.type === 'complete') this.commit({ scan: this.scanOf(event.scan), scanning: undefined });
      else if (event.type === 'progress') {
        this.commit({ scanning: { done: event.done, total: event.total } });
        refresh();
      }
    });
    refresh();
  }
}

/** The reader's colour for a state, White when the owner played neither side. */
export function referenceSide(state: Pick<ReviewState, 'reader'>): Color {
  return state.reader ?? 'white';
}
